/**
 * `Context_Registry.ts` — port of `AI/Registry/Context_Registry.cs`.
 *
 * Web slice: `Prompt_SceneContextAgent` (hierarchy helper agent over the
 * Responses API) is deferred; `Get_Scene_Context` runs local string matching
 * only. `Get_Part_Context` builds local context and an enriched prompt, then
 * delegates the single document lookup to its registered
 * `Prompt_Search_Docs` companion; the Knowledge tool family itself lives in
 * `Docs_Registry.ts`.
 *
 * Unity registers the context tools with `Enabled = false` as the
 * pre-refresh default; `refreshToolAvailability(hasProject)` flips them on
 * once a project is open. Mirrored here.
 *
 * Copy deviation (AI-1358): `Get_Part_Context`'s `labelWhenActive` is
 * "Thinking...", the same words as `useSpark`'s generic thinking placeholder,
 * instead of the "Searching part context..." it was ported with. The label is
 * only the Spark prompt's placeholder while the tool runs (`toRealtimeTool`
 * does not send it to the model). Do not "restore parity" on this one string.
 */

import { AgentToolNames } from "./agentToolNames";
import {
  type AIAgentTool,
  buildParams,
  stringList,
  stringParam,
} from "./AIAgentToolRegistryModel";

export function addContextTools(tools: AIAgentTool[]): void {
  tools.push(
    {
      name: AgentToolNames.GetProjectContext,
      category: "Context",
      description:
        "Returns project-level info: project name, scenes (with notes), models per scene, and the currently active scene. A model entry may carry an animation indicator, e.g. '(hasAnimation:true)'; the animation's names are listed by Get_Scene_Context. Always call fresh - project context can change at any time.",
      humanDescription:
        "View project details, scene names, notes, models, and the current scene.",
      parameters: buildParams(),
      enabled: false,
      writerOnly: false,
    },
    {
      name: AgentToolNames.GetSceneContext,
      category: "Context",
      labelWhenActive: "Looking through scene...",
      description:
        "Looks up the current scene structure (parts, top-level models/root paths, selection sets, text boxes). " +
        "With SearchQuery, the helper tries fast local string matching against the hierarchy and returns the relevant lines. " +
        "With neither SearchQuery nor prompt, returns the full scene context. " +
        "Use this primarily to locate/disambiguate parts or models, including generic references like 'the model', or resolve paths for downstream tools. " +
        "When the user names a part, top-level model, selection set, or text box, call this to resolve that explicit name even if User Context currently points at, last pointed at, or selected a matching object. " +
        "Selecting a model is not required before model-level operations; identify the target root model with this tool instead. " +
        "Response line formats are documented in the system prompt under 'Hierarchy Path Format'. " +
        "If a target name matches a selection set name, the selection-set line is returned and should be used directly. " +
        "If one relevant match is returned for a named target, treat it as resolved; copy the path verbatim into downstream tool args.",
      humanDescription:
        "Find matching parts, top-level models, selection sets, or text boxes in the scene hierarchy, or return the full hierarchy when needed.",
      parameters: buildParams({
        SearchQuery: stringList(
          "Raw search terms for fast local hierarchy matching, e.g. ['Part657'], ['Assembly A'], ['Label B'].",
        ),
        prompt: stringParam(
          "Short request for the subset you want, e.g. 'Return objects identified as <what the user named>'. Do NOT specify response fields/formatting. Do NOT include the purpose ('for move_parts', 'to hide').",
        ),
      }),
      enabled: false,
      writerOnly: false,
    },
    {
      name: AgentToolNames.GetPartContext,
      category: "Context",
      labelWhenActive: "Thinking...",
      description:
        "Returns one part-enriched Prompt_Search_Docs result plus local hierarchy and alias context for a specific part. The runtime uses the local part result to enrich the registered Prompt_Search_Docs companion, which searches with all the part's names together and once more for each alias, and returns the results grouped by name, then returns document passages as rank 1 and local context as rank 2. Do not call Prompt_Search_Docs separately for the same part question. If local part context is unavailable, the companion still searches with the original question. "
        + "NOT A SUBSTITUTE FOR THE ANSWER SKILL: it never measures size or height, never counts and never recalls. If Answer is listed under Available Skills and not loaded, call Load_Skill for it BEFORE this tool on any factual question, and follow its routing - which may never reach this tool. "
        + "SILENT CALL - the rules above are your reasoning, not something to say: never announce that you are about to look the part up, explain that you are checking the part and the documents together, or set out what you will tell them once the result is in. Call with nothing spoken, and speak once, with the answer. "
        + "Provide partPath. The prompt is a standalone question about the fact the answer needs, asked with a question word (what, where, how high, how many) and naming the resolved part name or alias, with what the user's question left implicit filled in, such as the property your previous answer was about. A document states requirements and values, never verdicts, so a yes/no or comparison question searched as spoken matches nothing - ask for the requirement or value itself.",
      humanDescription:
        "Search documents once with part details and return local part context.",
      parameters: buildParams({
        prompt: stringParam(
          "A standalone question about the fact needed, with a question word and the resolved part name or alias.",
          false,
        ),
        partPath: stringParam("Hierarchy path from Get_Scene_Context (e.g. '0,1,2').", false),
      }),
      enabled: false,
      writerOnly: false,
    },
    {
      name: AgentToolNames.TakeScreenshot,
      category: "Context",
      description: `Captures the user's current Campfire view and sends the image to the AI agent. Use to see what the user is looking at or which UI elements are visible (e.g. 'what am I pointing at?', 'what is this?', 'what do you see?'). For AI inspection only - does NOT create scene content.`,
      humanDescription: "Capture the user's current view for AI inspection only.",
      parameters: buildParams(),
      enabled: true,
      writerOnly: false,
    },
  );
}
